import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const SEO = ({ title, description, keywords, canonicalUrl }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [pagePath, setPagePath] = useState('home');
  
  // Determine which page we're on based on the path
  useEffect(() => {
    const path = location.pathname;
    if (path === '/') {
      setPagePath('home');
    } else {
      // Remove leading slash and extract first path segment
      const pathSegment = path.substring(1).split('/')[0];
      
      // Map path segments to translation keys
      const pathToKey = {
        'about': 'about',
        'freelanch': 'meals',
        'help': 'help',
        'needhelp': 'needHelp',
        'report': 'reports',
        'contacts': 'contacts',
        'parteners': 'partners',
        'politic': 'privacy',
        'donate': 'donate',
        'formedia': 'forMedia',
        'news': 'news'
      };
      
      setPagePath(pathToKey[pathSegment] || 'home');
    }
  }, [location.pathname]);
  
  // Get page-specific or default title and description from translations
  const pageTitle = t(`seo.pages.${pagePath}.title`, { defaultValue: t('seo.defaultTitle') });
  const pageDescription = t(`seo.pages.${pagePath}.description`, { defaultValue: t('seo.defaultDescription') });
  
  const defaultTitle = t('seo.defaultTitle');
  const defaultDescription = t('seo.defaultDescription');
  const defaultKeywords = t('seo.defaultKeywords');
  
  // Use explicitly provided values if available, otherwise use page-specific translations
  const metaTitle = title || pageTitle || defaultTitle;
  const metaDescription = description || pageDescription || defaultDescription;
  const metaKeywords = keywords || defaultKeywords;
  
  const currentUrl = window.location.href;
  const canonical = canonicalUrl || currentUrl;
  
  // Generate language alternate links
  const languages = ['ru', 'ro', 'en'];
  const alternateLinks = languages.map(lang => {
    const url = new URL(currentUrl);
    const pathParts = url.pathname.split('/');
    
    // If the path already has a language prefix, replace it
    if (languages.includes(pathParts[1])) {
      pathParts[1] = lang;
    } else {
      // Otherwise insert the language prefix
      pathParts.splice(1, 0, lang);
    }
    
    url.pathname = pathParts.join('/');
    return {
      hrefLang: lang,
      href: url.toString()
    };
  });

  return (
    <Helmet>
      <html lang={i18n.language} />
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonical} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary" />
      <meta property="twitter:url" content={currentUrl} />
      <meta property="twitter:title" content={metaTitle} />
      <meta property="twitter:description" content={metaDescription} />
      
      {/* Alternate language versions */}
      {alternateLinks.map(link => (
        <link 
          key={link.hrefLang} 
          rel="alternate" 
          hrefLang={link.hrefLang} 
          href={link.href} 
        />
      ))}
    </Helmet>
  );
};

export default SEO;
